import React, { memo } from 'react'
import { View, useWindowDimensions, Image, Text } from 'react-native'
import { useTheme } from 'styled-components/native'
import RenderHtml, { useInternalRenderer } from 'react-native-render-html'
import { iframeModel, useHtmlIframeProps, HTMLIframe } from '@native-html/iframe-plugin'
import WebView from 'react-native-webview'
import merge from 'lodash/merge'
import { parse } from 'marked'
import Constants from 'expo-constants'

import { isWeb } from 'APP/Helpers/checkPlatform'

import Styles from './style'

function CustomImageRenderer(style) {
  return function ImageWrapper(props) {
    const { rendererProps } = useInternalRenderer('img', props)

    return (
      <View style={style.imageWrapper}>
        <Image style={style.image} source={rendererProps.source} />
      </View>
    )
  }
}

function CustomHeader(style) {
  return function Header(props) {
    return <Text style={style}>{props.tnode?.domNode?.children?.[0]?.data}</Text>
  }
}

function CustomEmphasis(props) {
  return <Text style={Styles.emphasis}>{props.tnode?.domNode?.children?.[0]?.data}</Text>
}

function CustomParagraph(mergedStyles) {
  return function ParagraphRenderer({ TDefaultRenderer, tnode, ...props }) {
    const siblings = tnode?.parent?.children
    const isLastSibling = !siblings || siblings.indexOf(tnode) === siblings.length - 1
    const style = isLastSibling ? [mergedStyles.p, { marginBottom: 0 }] : mergedStyles.p

    return <TDefaultRenderer {...props} tnode={tnode} style={style} />
  }
}

const customHTMLElementModels = {
  iframe: iframeModel,
}

const IframeRenderer = function IframeRenderer(props) {
  const theme = useTheme()
  const iframeProps = useHtmlIframeProps(props)

  return isWeb() ? (
    <iframe
      {...iframeProps.htmlAttribs}
      height={iframeProps.style[1].height}
      width="100%"
      style={{
        borderWidth: 0,
        marginBottom: theme.metrics.baseMargin / 2,
      }}
    />
  ) : (
    <HTMLIframe
      {...iframeProps}
      style={[
        iframeProps.style,
        { width: '100%', height: 400, marginBottom: theme.metrics.baseMargin / 2 },
      ]}
    />
  )
}

const DialogueMarkdown = ({ markdown, styles, containerStyles }) => {
  const theme = useTheme()
  const { width } = useWindowDimensions()

  if (!markdown) {
    return null
  }

  const injectedJavaScript = `
    var node = document.createElement('style');
    var rules = ""
    rules += "html, body { background: ${theme.colors.background}; }";
    rules += "body {  color: ${theme.colors.text}; }";
    rules += "a { color: ${theme.colors.accent}; }"
    node.innerHTML = rules;
    document.querySelector('HEAD').appendChild(node);
  `

  const mergedStyles = styles ? merge({}, Styles, styles) : Styles

  const systemFonts = [
    ...Constants.systemFonts,
    theme.fonts.fontTypes.base,
    theme.fonts.fontTypes.bold,
    theme.fonts.fontTypes.button,
    theme.fonts.fontTypes.header,
    theme.fonts.fontTypes.light,
  ]

  const html = parse(markdown, { breaks: true })

  const renderers = {
    img: CustomImageRenderer(mergedStyles),
    p: CustomParagraph(mergedStyles),
    h1: CustomHeader(Styles.h1),
    h2: CustomHeader(Styles.h2),
    h3: CustomHeader(Styles.h3),
    h4: CustomHeader(Styles.h4),
    h5: CustomHeader(Styles.h5),
    h6: CustomHeader(Styles.h6),
    em: CustomEmphasis,
    iframe: IframeRenderer,
  }

  return (
    <View style={containerStyles}>
      <RenderHtml
        contentWidth={width}
        source={{ html }}
        WebView={WebView}
        tagsStyles={mergedStyles}
        systemFonts={systemFonts}
        renderers={renderers}
        customHTMLElementModels={customHTMLElementModels}
        renderersProps={{
          iframe: {
            scalesPageToFit: false,
            webViewProps: {
              scalesPageToFit: false,
              injectedJavaScript,
              javaScriptEnabledAndroid: true,
              style: {
                flex: 1,
                backgroundColor: theme.colors.background,
              },
            },
          },
        }}
      />
    </View>
  )
}

export default memo(DialogueMarkdown)
